import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Tabs,
  Tab,
  Paper,
  Stack,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  LinearProgress,
} from '@mui/material'
import {
  WhatsApp as WhatsAppIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  Storage as StorageIcon,
  Memory as MemoryIcon,
  Warning as WarningIcon,
  Circle as CircleIcon,
  Replay as RetryIcon,
  AccessTime as TimeIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material'
import { format, parseISO } from 'date-fns'
import apiClient from '../services/api'

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'

const STATUS_COLOR: Record<string, ChipColor> = {
  PENDING: 'default',
  QUEUED: 'info',
  SENT: 'primary',
  DELIVERED: 'success',
  OPENED: 'success',
  CLICKED: 'success',
  READ: 'success',
  FAILED: 'error',
  BOUNCED: 'error',
  CANCELLED: 'default',
}

const STATUS_ICON: Record<string, React.ReactNode> = {
  DELIVERED: <CheckCircleIcon sx={{ fontSize: 14 }} />,
  SENT: <SendIcon sx={{ fontSize: 14 }} />,
  FAILED: <ErrorIcon sx={{ fontSize: 14 }} />,
  PENDING: <ScheduleIcon sx={{ fontSize: 14 }} />,
  QUEUED: <ScheduleIcon sx={{ fontSize: 14 }} />,
}

const EVENT_LABELS: Record<string, string> = {
  ORDER_PLACED: 'Order Placed',
  ORDER_CONFIRMED: 'Order Confirmed',
  ORDER_SHIPPED: 'Order Shipped',
  ORDER_DELIVERED: 'Order Delivered',
  ORDER_CANCELLED: 'Order Cancelled',
  PAYMENT_RECEIVED: 'Payment Received',
  PAYMENT_FAILED: 'Payment Failed',
  INVOICE_GENERATED: 'Invoice',
  STOCK_ALERT: 'Low Stock Alert',
  REMARKETING: 'Remarketing',
  LEAD_FOLLOWUP: 'Lead Follow-up',
  ABANDONED_CART: 'Abandoned Cart',
  CUSTOM: 'Custom',
}

const QUEUE_COLORS: Record<string, string> = {
  notifications: '#7c3aed',
  reports: '#2563eb',
  maintenance: '#0284c7',
  inventory_alerts: '#059669',
  inventory_analytics: '#16a34a',
  credit_management: '#d97706',
  celery: '#6b7280',
}

interface DeliveryLog {
  id: string
  recipient_phone: string
  event_type: string
  status: string
  content: string
  queued_at: string | null
  sent_at: string | null
  delivered_at: string | null
  failed_at: string | null
  retry_count: number
  error_message: string | null
  error_code: string | null
  provider_message_id: string | null
  created_at: string
  customer_id: string | null
}

interface DashboardStats {
  messages_sent: { today: number; this_week: number; this_month: number }
  delivery_rates: { success_rate: number; failure_rate: number }
  recent_failed_messages: Array<{
    id: string
    recipient_phone: string
    event_type: string
    error_message: string | null
    failed_at: string | null
    retry_count: number
  }>
  message_type_breakdown: {
    by_category: { transactional: number; promotional: number }
  }
}

interface WorkerStatus {
  redis: {
    connected: boolean
    version?: string
    uptime_seconds?: number
    used_memory_human?: string
    connected_clients?: number
    error?: string
  }
  queues: Record<string, number>
  total_pending_tasks: number
  workers: {
    available: boolean
    total_workers?: number
    workers?: Array<{ name: string; status: string; active_tasks: number; scheduled_tasks: number }>
    error?: string
  }
}

interface TabPanelProps {
  children?: React.ReactNode
  value: number
  index: number
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null
}

export default function WhatsAppDeliveryPage() {
  const [tab, setTab] = useState(0)

  // Delivery logs state
  const [logs, setLogs] = useState<DeliveryLog[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(25)
  const [statusFilter, setStatusFilter] = useState('')
  const [eventFilter, setEventFilter] = useState('')
  const [logsLoading, setLogsLoading] = useState(false)
  const [logsError, setLogsError] = useState<string | null>(null)
  const [retryingId, setRetryingId] = useState<string | null>(null)

  // Dashboard stats state
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [statsLoading, setStatsLoading] = useState(false)

  // Worker status state
  const [workerStatus, setWorkerStatus] = useState<WorkerStatus | null>(null)
  const [workerLoading, setWorkerLoading] = useState(false)
  const [workerError, setWorkerError] = useState<string | null>(null)
  const [workerLastFetched, setWorkerLastFetched] = useState<Date | null>(null)

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true)
    setLogsError(null)
    try {
      const params: Record<string, string | number> = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      }
      if (statusFilter) params.status_filter = statusFilter
      if (eventFilter) params.alert_type = eventFilter
      const res = await apiClient.get('/admin/whatsapp/delivery-logs', { params })
      setLogs(res.data.items)
      setTotal(res.data.total)
    } catch (e: any) {
      setLogsError(e?.response?.data?.detail || 'Failed to load delivery logs')
    } finally {
      setLogsLoading(false)
    }
  }, [page, pageSize, statusFilter, eventFilter])

  const fetchStats = useCallback(async () => {
    setStatsLoading(true)
    try {
      const res = await apiClient.get('/admin/whatsapp/dashboard')
      setStats(res.data)
    } catch {
      // non-critical
    } finally {
      setStatsLoading(false)
    }
  }, [])

  const fetchWorkerStatus = useCallback(async () => {
    setWorkerLoading(true)
    setWorkerError(null)
    try {
      const res = await apiClient.get('/admin/system/worker-status')
      setWorkerStatus(res.data)
      setWorkerLastFetched(new Date())
    } catch (e: any) {
      setWorkerError(e?.response?.data?.detail || 'Failed to load worker status')
    } finally {
      setWorkerLoading(false)
    }
  }, [])

  useEffect(() => {
    if (tab === 0) {
      fetchStats()
      fetchLogs()
    } else {
      fetchWorkerStatus()
    }
  }, [tab, fetchLogs, fetchStats, fetchWorkerStatus])

  const handleRetry = async (logId: string) => {
    setRetryingId(logId)
    try {
      await apiClient.post(`/admin/whatsapp/delivery-logs/${logId}/retry`)
      await fetchLogs()
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Retry failed')
    } finally {
      setRetryingId(null)
    }
  }

  const formatTs = (ts: string | null) => {
    if (!ts) return '—'
    try { return format(parseISO(ts), 'dd MMM, HH:mm') } catch { return ts }
  }

  const uptimeFmt = (seconds?: number) => {
    if (!seconds) return '—'
    const d = Math.floor(seconds / 86400)
    const h = Math.floor((seconds % 86400) / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    if (d > 0) return `${d}d ${h}h`
    if (h > 0) return `${h}h ${m}m`
    return `${m}m`
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <WhatsAppIcon sx={{ color: '#25D366', fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>WhatsApp & Queue Monitor</Typography>
            <Typography variant="body2" color="text.secondary">
              Message delivery logs, worker health, and Redis queue depths
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Delivery Logs" icon={<WhatsAppIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Queue & Workers" icon={<StorageIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* ── TAB 0: Delivery Logs ── */}
      <TabPanel value={tab} index={0}>
        {/* Stats cards */}
        {stats && (
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">Today</Typography>
                  <Typography variant="h4" fontWeight={700}>{stats.messages_sent.today}</Typography>
                  <Typography variant="caption">messages sent</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">This Month</Typography>
                  <Typography variant="h4" fontWeight={700}>{stats.messages_sent.this_month}</Typography>
                  <Typography variant="caption">messages sent</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">Success Rate</Typography>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    {stats.delivery_rates.success_rate}%
                  </Typography>
                  <Typography variant="caption">last 30 days</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">Failure Rate</Typography>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    color={stats.delivery_rates.failure_rate > 10 ? 'error.main' : 'text.primary'}
                  >
                    {stats.delivery_rates.failure_rate}%
                  </Typography>
                  <Typography variant="caption">last 30 days</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
        {statsLoading && !stats && <LinearProgress sx={{ mb: 2 }} />}

        {/* Filters */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1) }} label="Status">
              <MenuItem value="">All</MenuItem>
              {['PENDING', 'QUEUED', 'SENT', 'DELIVERED', 'FAILED', 'BOUNCED', 'OPENED'].map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Event Type</InputLabel>
            <Select value={eventFilter} onChange={e => { setEventFilter(e.target.value); setPage(1) }} label="Event Type">
              <MenuItem value="">All</MenuItem>
              {Object.entries(EVENT_LABELS).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            startIcon={<RefreshIcon />}
            size="small"
            variant="outlined"
            onClick={fetchLogs}
            disabled={logsLoading}
          >
            Refresh
          </Button>
        </Stack>

        {logsError && <Alert severity="error" sx={{ mb: 2 }}>{logsError}</Alert>}

        <TableContainer component={Paper} variant="outlined">
          {logsLoading && <LinearProgress />}
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Content Preview</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Retries</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Error</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && !logsLoading && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">No messages found</Typography>
                  </TableCell>
                </TableRow>
              )}
              {logs.map(log => (
                <TableRow key={log.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="caption">{formatTs(log.created_at)}</Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 13 }}>
                    {log.recipient_phone}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {EVENT_LABELS[log.event_type] || log.event_type}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={log.status}
                      color={STATUS_COLOR[log.status] || 'default'}
                      icon={STATUS_ICON[log.status] as any}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography
                      variant="caption"
                      sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                    >
                      {log.content}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {log.retry_count > 0 ? (
                      <Chip size="small" label={log.retry_count} color="warning" variant="outlined" />
                    ) : '—'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    {log.error_message ? (
                      <Tooltip title={log.error_message}>
                        <Typography variant="caption" color="error.main" sx={{ cursor: 'help' }}>
                          {log.error_code || log.error_message.slice(0, 30)}
                        </Typography>
                      </Tooltip>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    {log.status === 'FAILED' && log.retry_count < 3 && (
                      <Tooltip title="Retry sending">
                        <IconButton
                          size="small"
                          onClick={() => handleRetry(log.id)}
                          disabled={retryingId === log.id}
                        >
                          {retryingId === log.id ? <CircularProgress size={14} /> : <RetryIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {total > pageSize && (
          <Stack alignItems="center" mt={2}>
            <Pagination
              count={Math.ceil(total / pageSize)}
              page={page}
              onChange={(_, p) => setPage(p)}
              color="primary"
              size="small"
            />
          </Stack>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {total} total messages
        </Typography>
      </TabPanel>

      {/* ── TAB 1: Queue & Workers ── */}
      <TabPanel value={tab} index={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1" fontWeight={600}>Redis & Celery Worker Status</Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            {workerLastFetched && (
              <Typography variant="caption" color="text.secondary">
                <TimeIcon sx={{ fontSize: 12, mr: 0.3, verticalAlign: 'middle' }} />
                Updated {format(workerLastFetched, 'HH:mm:ss')}
              </Typography>
            )}
            <Button
              startIcon={<RefreshIcon />}
              size="small"
              variant="outlined"
              onClick={fetchWorkerStatus}
              disabled={workerLoading}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>

        {workerLoading && <LinearProgress sx={{ mb: 2 }} />}
        {workerError && <Alert severity="error" sx={{ mb: 2 }}>{workerError}</Alert>}

        {workerStatus && (
          <Grid container spacing={2}>
            {/* Redis info card */}
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <StorageIcon color={workerStatus.redis.connected ? 'success' : 'error'} />
                    <Typography variant="subtitle2" fontWeight={600}>Redis Server</Typography>
                    <Chip
                      size="small"
                      label={workerStatus.redis.connected ? 'Connected' : 'Offline'}
                      color={workerStatus.redis.connected ? 'success' : 'error'}
                    />
                  </Stack>

                  {workerStatus.redis.connected ? (
                    <Stack spacing={1}>
                      {[
                        ['Version', workerStatus.redis.version],
                        ['Uptime', uptimeFmt(workerStatus.redis.uptime_seconds)],
                        ['Memory Used', workerStatus.redis.used_memory_human],
                        ['Clients', workerStatus.redis.connected_clients],
                        ['Pending Tasks', workerStatus.total_pending_tasks],
                      ].map(([label, value]) => (
                        <Stack key={label as string} direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">{label}</Typography>
                          <Typography variant="caption" fontWeight={600}>{value ?? '—'}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  ) : (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {workerStatus.redis.error || 'Could not connect to Redis'}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Queue depths card */}
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <MemoryIcon color="primary" />
                    <Typography variant="subtitle2" fontWeight={600}>Queue Depths</Typography>
                    <Tooltip title="Number of tasks waiting to be processed in each queue">
                      <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </Tooltip>
                  </Stack>
                  <Stack spacing={1.5}>
                    {Object.entries(workerStatus.queues)
                      .filter(([q]) => q !== 'celery')
                      .map(([queueName, depth]) => (
                        <Box key={queueName}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <CircleIcon sx={{ fontSize: 8, color: QUEUE_COLORS[queueName] || '#6b7280' }} />
                              <Typography variant="caption">{queueName}</Typography>
                            </Stack>
                            <Chip
                              size="small"
                              label={depth}
                              color={depth > 10 ? 'warning' : depth > 0 ? 'info' : 'default'}
                              variant={depth > 0 ? 'filled' : 'outlined'}
                              sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                            />
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min((depth / 20) * 100, 100)}
                            color={depth > 10 ? 'warning' : 'primary'}
                            sx={{ height: 3, borderRadius: 2 }}
                          />
                        </Box>
                      ))}
                    {workerStatus.total_pending_tasks === 0 && (
                      <Alert severity="success" icon={<CheckCircleIcon fontSize="small" />} sx={{ py: 0.5 }}>
                        All queues empty — workers are idle
                      </Alert>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Workers card */}
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <CircleIcon
                      sx={{
                        fontSize: 14,
                        color: workerStatus.workers.available && (workerStatus.workers.total_workers ?? 0) > 0
                          ? 'success.main'
                          : 'error.main',
                      }}
                    />
                    <Typography variant="subtitle2" fontWeight={600}>Celery Workers</Typography>
                    {workerStatus.workers.available ? (
                      <Chip
                        size="small"
                        label={`${workerStatus.workers.total_workers ?? 0} online`}
                        color={(workerStatus.workers.total_workers ?? 0) > 0 ? 'success' : 'error'}
                      />
                    ) : (
                      <Chip size="small" label="Unavailable" color="error" />
                    )}
                  </Stack>

                  {!workerStatus.workers.available && (
                    <Alert severity="warning">
                      {workerStatus.workers.error || 'Celery workers not reachable'}
                    </Alert>
                  )}

                  {workerStatus.workers.available && (workerStatus.workers.workers ?? []).length === 0 && (
                    <Alert severity="error">
                      No active workers found — tasks will not be processed!
                    </Alert>
                  )}

                  <Stack spacing={1.5} mt={1}>
                    {(workerStatus.workers.workers ?? []).map(w => (
                      <Paper key={w.name} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Box sx={{ maxWidth: '60%' }}>
                            <Typography variant="caption" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                              {w.name.split('@')[1] || w.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {w.name.split('@')[0]}
                            </Typography>
                          </Box>
                          <Chip size="small" label={w.status} color={w.status === 'online' ? 'success' : 'default'} />
                        </Stack>
                        <Stack direction="row" spacing={2} mt={1}>
                          <Typography variant="caption" color="text.secondary">
                            Active: <strong>{w.active_tasks}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Scheduled: <strong>{w.scheduled_tasks}</strong>
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>

                  <Box mt={2} sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      To restart workers, SSH into the server and run:
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1, mt: 0.5, bgcolor: 'action.hover' }}>
                      <Typography variant="caption" fontFamily="monospace" display="block">
                        systemctl restart b2b-celery-worker
                      </Typography>
                      <Typography variant="caption" fontFamily="monospace" display="block">
                        systemctl restart b2b-celery-beat
                      </Typography>
                    </Paper>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Beat schedule info */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={600} mb={1.5}>Scheduled Tasks (Beat)</Typography>
                  <Grid container spacing={1}>
                    {[
                      { name: 'Low Stock Alerts', schedule: 'Every 6 hours', queue: 'inventory_alerts' },
                      { name: 'Sales Trends Cache', schedule: 'Daily 2 AM', queue: 'inventory_analytics' },
                      { name: 'Customer Aging', schedule: 'Daily 1 AM', queue: 'credit_management' },
                      { name: 'Expired Carts Cleanup', schedule: 'Every hour', queue: 'maintenance' },
                      { name: 'Abandoned Cart Nudges', schedule: 'Every 30 min', queue: 'maintenance' },
                      { name: 'Stock Reservation Cleanup', schedule: 'Every 5 min', queue: 'maintenance' },
                      { name: 'Audit Log Cleanup', schedule: 'Weekly Sunday', queue: 'maintenance' },
                    ].map(t => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={t.name}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" fontWeight={600} display="block">{t.name}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">{t.schedule}</Typography>
                          <Box mt={0.5}>
                            <CircleIcon sx={{ fontSize: 8, color: QUEUE_COLORS[t.queue] || '#6b7280', mr: 0.5, verticalAlign: 'middle' }} />
                            <Typography variant="caption" color="text.secondary">{t.queue}</Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {!workerStatus && !workerLoading && !workerError && (
          <Alert severity="info">Click Refresh to load worker status.</Alert>
        )}
      </TabPanel>
    </Box>
  )
}
